import type { Agent } from '@studio/models/agent';
import type { Room } from '@studio/models/room';
import { broker, locateProxy } from '#~/agent/index.js';
import * as Commands from '#~/device/commands/index.js';
import type { Device } from '#~/device/index.js';
import { logger } from '#~/logger/index.js';
import { pbToJsDevice } from '#~/protobuf/index.js';
import type { ReplaceDeviceOutcome } from './replace-outcome.js';
import { Protocol as ControlProtocol } from '@studio/models/control';
import { DefaultModelId } from '@studio/models/device';
import { watchConnectionVerification } from './connection-verification.js';

export type { ReplaceDeviceOutcome } from './replace-outcome.js';
export { interpretReplaceOutcome } from './replace-outcome.js';

/** Shape `pbToJsDevice` expects for the agent state (protobuf `State` slice). */
type PbAgentState = Parameters<typeof pbToJsDevice>[2];

/**
 * Extract the camera's connection host (its IP / hostname) from whichever
 * aspect carries it. Video (RTSP) is preferred because that is what a
 * promoted device exposes; control (ONVIF / HTTP / VISCA-IP) is the
 * fallback for the ONVIF-placeholder case where the frozen `replacement`
 * only ever had a control aspect (see `replaceDevice` below).
 *
 * ENG-4356: we key on the HOST, not host+port. The port is NOT stable
 * across the placeholder→promotion transition — a brandless ONVIF
 * placeholder connects on the ONVIF control port (e.g. 80/2020) while the
 * promoted device streams on the RTSP video port (554). The camera's host
 * IP, however, is identical on both sides (it is the same physical camera
 * on the same agent), so it is the robust linkage signal. An agent does
 * not manage two distinct cameras at the same host in the multi-add flow.
 *
 * @param device - Device to inspect.
 * @returns Lower-cased host string, or `undefined` when no aspect carries one.
 */
const connectionHost = (device: Device): string | undefined => {
  const videoConfig = device.video?.config;

  if (videoConfig && 'host' in videoConfig && videoConfig.host) {
    return String(videoConfig.host).toLowerCase();
  }

  const controlConfig = device.control?.config;

  if (controlConfig && 'host' in controlConfig && controlConfig.host) {
    return String(controlConfig.host).toLowerCase();
  }

  return undefined;
};

/**
 * Resolve the id of the CURRENTLY-live device that `replacement` targets,
 * reconciling the frozen id the Advanced-Settings dialog captured against
 * the agent's live state.
 *
 * ENG-4356: `replacement.id` comes from a `structuredClone` frozen when the
 * dialog opened (see `store/provider.tsx`: `lifecycle.original` /
 * `candidate`). For a brandless ONVIF placeholder, an ONVIF `GetProfiles`
 * auth probe PROMOTES that transient placeholder into a real device with a
 * NEW id after the freeze — so the frozen id is stale. `Commands.update`
 * cannot carry model/driver config (it only mutates `name`), so we must
 * reconcile the id here rather than switch commands.
 *
 * Resolution order:
 *   1. A live device already exists at `replacement.id` → the id is current
 *      (plain-RTSP / no-promotion path, e.g. Panasonic). `locateProxy` can
 *      return an agent proxy from its location cache even after the device
 *      was removed, so we additionally confirm the device is present in
 *      `state.control.device`.
 *   2. No live device at `replacement.id` → search the SAME agent's live
 *      devices for one whose connection host matches (the promoted device).
 *   3. Nothing matches → `undefined` (caller must NOT mint a phantom).
 *
 * @param agentId - Agent that owns the device.
 * @param replacement - Frozen replacement device from the dialog.
 * @returns The live device id to target, or `undefined` when none is found.
 */
const resolveLiveDeviceId = (
  agentId: Agent['id'],
  replacement: Device
): Device['id'] | undefined => {
  // (1) Is the frozen id still live? locateProxy can hand back a cached
  // proxy whose device is already gone, so confirm the device really exists.
  const direct = locateProxy(replacement.id);

  if (direct?.state.control?.device?.[replacement.id]) {
    return replacement.id;
  }

  // (2) The frozen id is stale (promoted away). Find the promoted device on
  // the same agent by connection host.
  const proxy = broker.acquire(agentId);
  const targetHost = connectionHost(replacement);

  if (!proxy || !targetHost) {
    return undefined;
  }

  for (const id of Object.keys(proxy.state.control?.device ?? {})) {
    // Already established above that replacement.id is not live; skip it.
    if (id === replacement.id) {
      continue;
    }

    // Reuse the canonical protobuf→JS converter so host extraction matches
    // the rest of the app (think-in-systems: wrap, don't duplicate).
    const live = pbToJsDevice(agentId, id, <PbAgentState>(<unknown>proxy.state), proxy.embedded);

    if (live && connectionHost(live) === targetHost) {
      return live.id;
    }
  }

  // (3) No live device matches.
  return undefined;
};

/**
 * Replace the existing device with the specified device.
 *
 * ENG-4356: reconcile the frozen `replacement.id` against the agent's live
 * state BEFORE mutating. Trusting the frozen id created a duplicate tile
 * stuck in "Connecting": when a brandless ONVIF placeholder was promoted to
 * a real device (new id) after the dialog opened, `remove(staleId)` no-oped
 * and `add(staleId, {force})` minted a SECOND device. We now target the
 * live id so the promoted device is UPDATED in place.
 *
 * @param roomId - Room ID.
 * @param agentId - Agent ID.
 * @param replacement - Replacement device.
 * @param token - The token.
 * @returns Whether the reload bound the device — `{ok:false}` reports the two
 *   failure states the reload used to swallow (see {@link ReplaceDeviceOutcome}).
 */
export const replaceDevice = async (
  roomId: Room['id'],
  agentId: Agent['id'],
  replacement: Device,
  token: string,
  options?: { verifyConnection?: boolean }
): Promise<ReplaceDeviceOutcome> => {
  const liveId = resolveLiveDeviceId(agentId, replacement);

  if (liveId === undefined) {
    // ENG-4356: no live device to update — do NOT `add(force)` the frozen id
    // (that is exactly what minted the orphan "Connecting" duplicate). Skipping
    // the mutation is the ONLY safe action here, so we still return. But log at
    // ERROR, not warning (ENG review §9): the warn was easy to miss, which made
    // downstream "device never bound" failures (e.g. multi-add-config.spec) hard
    // to attribute. Loud + attributable, without throwing — this is a user-facing
    // Save, and the edited device simply being gone is a legit transient, not a
    // crash-worthy fault.
    logger.error(
      `[ENG-4356] replaceDevice: NO LIVE DEVICE for frozen id ${replacement.id} on agent ${agentId} — Save persisted NOTHING (the device is no longer live; skipping the add to avoid an orphaned duplicate). If a spec/user expected this device bound, this log is the reason.`
    );

    // Report the skip so a reload caller can surface it; the log alone was
    // easy to miss (ENG review §9) and callers had no signal to act on.
    return { ok: false, reason: 'no-live-device' };
  }

  if (liveId !== replacement.id) {
    // ENG-4356: the frozen id differs from the live id (placeholder was
    // promoted). Warn so the mismatch is never silent again, then target
    // the live id.
    logger.warning(
      `[ENG-4356] replaceDevice: frozen id ${replacement.id} != live id ${liveId} (placeholder promoted); targeting the live device.`
    );
  }

  // Target the reconciled live id. Only clone when the id actually changed.
  const target: Device = liveId === replacement.id ? replacement : { ...replacement, id: liveId };

  // Snapshot the CURRENTLY-live device BEFORE removing it so a failed re-add
  // can roll back to exactly what was there, rather than leaving the camera
  // gone. Read the agent proxy's live state and reuse the canonical
  // protobuf→JS converter (same as the id reconciliation above).
  // Per `think-in-systems` (ai.knowledge/global): replaceDevice is correct by
  // construction — it never leaves the device missing on a failed reload.
  const priorProxy = broker.acquire(agentId);
  const prior: Device | undefined = priorProxy?.state.control?.device?.[liveId]
    ? (pbToJsDevice(
        agentId,
        liveId,
        <PbAgentState>(<unknown>priorProxy.state),
        priorProxy.embedded
      ) ?? undefined)
    : undefined;

  const verification = options?.verifyConnection
    ? watchDeviceConnection(agentId, target)
    : undefined;

  try {
    // Remove the existing device. The 5th arg (`replaceDriven`) suppresses the
    // detached ~250ms re-removal retry in `commands/remove.ts`: we re-add the
    // SAME id immediately below, and that stray timer would otherwise delete
    // the re-added (or rolled-back) device.
    // Per `reference-rules-in-comments` (ai.knowledge/global).
    await Commands.remove(target.id, token, true, true, true);
  } catch {
    // Remove is best-effort: an already-absent device is fine, and the add
    // below re-asserts the live config regardless.
  }

  try {
    // Re-create the device
    await Commands.add(roomId, agentId, target, token, { force: true });
    // The replacement has landed: status updates from here on belong to it, so a fresh status
    // verifies even when the old one never visibly disappeared (generation-aware freshness).
    verification?.commit();
  } catch {
    verification?.stop();
    // The re-add failed after the remove. Roll back to the pre-remove device so
    // a failed credential change never silently drops the camera. Report
    // `rolled-back` when that restore lands, `add-failed` when it does not.
    if (prior) {
      try {
        await Commands.add(roomId, agentId, prior, token, { force: true });
        // Prior device is back — new settings did not land. Distinct from
        // `add-failed` so callers do not tell the user to re-add a camera
        // that is still there.
        return { ok: false, reason: 'rolled-back' };
      } catch {
        // Rollback itself failed — nothing safe left to do here.
      }
    }

    return { ok: false, reason: 'add-failed' };
  }

  // Report the resolved live id (the one actually replaced) so the caller clears its hold, not the frozen/promoted-away id.
  return {
    ok: true,
    deviceId: liveId.toString(),
    ...(options?.verifyConnection
      ? {
          verification: (await verification?.wait()) ?? 'unverified',
          // Per-aspect detail so the UI can attribute a rejection to video or control.
          ...(verification ? { verificationAspects: verification.aspects() } : {})
        }
      : {})
  };
};

/** Start before a reload or new add so only that configuration's status can verify it. */
export const watchDeviceConnection = (
  agentId: Agent['id'],
  device: Device
): ReturnType<typeof watchConnectionVerification> | undefined => {
  const proxy = broker.acquire(agentId);
  if (!proxy) return undefined;
  return watchConnectionVerification(
    () => proxy.state.status?.device[device.id],
    (refresh) => {
      proxy.on('state.tx', refresh);
      return () => {
        proxy.off('state.tx', refresh);
      };
    },
    {
      video: !!device.video,
      control: !!device.control && !!device.modelId && device.modelId !== DefaultModelId,
      // See knowledge: state-sync-status-field-chain-movement-connectionstatus (ai.knowledge/platform)
      // — only the bridge's HTTP control driver writes a movement credential verdict; VISCA never does.
      controlConnectionOnly:
        device.control?.config.protocol === ControlProtocol.VISCA_IP ||
        device.control?.config.protocol === ControlProtocol.VISCA_SERIAL
    }
  );
};
